export interface CapturaRecurso {
  imagem: string;
  titulo: string;
  legenda: string;
}

export interface ApresentacaoRecurso {
  beneficios: string[];
  requisito?: string;
  previa?: CapturaRecurso & { outrasCapturas?: CapturaRecurso[] };
}

// Títulos de apresentação; slugs e nomes do catálogo permanecem inalterados.
export const TITULOS_RECURSOS: {[slug: string]: string} = {
  cardapio_digital: 'Cardápio Digital',
  dominio_proprio: 'Domínio Próprio',
  temas_personalizados: 'Temas Personalizados',
  pedidos_delivery: 'Pedidos Delivery',
  google_maps_taxas: 'Google Maps / Taxas automáticas',
  controle_fiado: 'Controle de Fiado',
  avaliacoes_pedidos: 'Avaliações de Pedidos',
  impressao_avancada: 'Impressão avançada automatizada',
  integracao_gateway_pagamento: 'Integração com Gateway de Pagamento',
  integracao_pdv_parceiros: 'Integração com PDVs Parceiros',
  integracao_delivery_parceiros: 'Integração com Deliverys Parceiros',
  integracao_marketplaces_delivery: 'Integração com Marketplaces de Delivery',
  cupons_avancados: 'Cupons e Promoções',
  marketing_relacionamento: 'Marketing e Relacionamento',
  notificacoes_app_proprio: 'Notificações do App Próprio',
  ferramentas_marketing: 'Ferramentas de Marketing Digital',
  atendimento_automatizado: 'Atendimento Automatizado',
  mia_atendente: 'MIA — Atendente Virtual',
  mia_v3: 'MIA V3 — Vendedora IA',
  notificacoes_whatsapp_pedidos: 'Notificações via WhatsApp',
  api_whatsapp_oficial: 'API Oficial do WhatsApp',
  operador_extra: 'Operador Extra',
  mesas_garcom: 'Mesas e Comandas',
  atendimento_garcom: 'Atendimento por Garçom',
  app_proprio: 'App Próprio',
  entregas_logistica: 'Entregas e Logística',
  gestao_avancada: 'Gestão Avançada',
  tabela_precos: 'Tabela de Preços',
  inteligencia_negocio: 'Inteligência do Negócio',
  analytics_cardapio: 'Analytics do Cardápio',
  pagamentos_eletronicos: 'Pagamentos Eletrônicos',
};

// Somente capturas verificadas da interface real. Nunca desenhar interfaces substitutas.
// Sem captura validada, manter benefícios e requisitos sem prévia visual.
// Origem e critérios de atualização: docs/recursos-capturas-reais.md.
export const APRESENTACOES_RECURSOS: { [slug: string]: ApresentacaoRecurso } = {
  api_whatsapp_oficial: {
    beneficios: ['Conecte o atendimento da loja à API oficial do WhatsApp.', 'Organize o uso do canal oficial na operação.', 'Conheça as condições de configuração e envio de mensagens.'],
    requisito: 'A equipe confirma a configuração do número, a compatibilidade e os custos do canal oficial.',
  },
  operador_extra: {
    beneficios: ['Amplie a quantidade de operadores da sua loja.', 'Distribua o atendimento entre os membros da equipe.', 'Mantenha acessos individuais conforme as permissões configuradas.'],
    requisito: 'A equipe confirma o limite atual de operadores e as condições para adicionar novos acessos.',
  },
  google_maps_taxas: {
    beneficios: ['Configure o cálculo de entrega com o Google Maps.', 'Considere a distância no cálculo das taxas.', 'Organize as regras de entrega da sua loja.'],
    requisito: 'Requer configuração da chave do Google Maps e das regras de entrega. A equipe orienta sobre os custos envolvidos.',
    previa: {
      imagem: 'assets/recursos/capturas/taxas-entrega-visao-geral.jpg',
      titulo: 'Entrega: taxas por distância e área de atendimento',
      legenda: 'Configuração real publicada na documentação. Faixas de distância e valores exibidos apenas como exemplo.',
    }
  },
  kds_cozinha: {
    beneficios: ['Separe a produção por área e etapa de preparo.', 'Veja tempo de espera, quantidades e observações em cada pedido.', 'Marque os itens prontos para acompanhar o andamento da cozinha.'],
    requisito: 'A equipe configura as áreas e etapas de produção para a operação da sua loja.',
    previa: {
      imagem: 'assets/recursos/capturas/kds-cozinha.png',
      titulo: 'Tela real do KDS: pedidos e etapas de produção',
      legenda: 'Captura da interface real de cozinha, publicada na documentação do sistema. Pedidos exibidos apenas como exemplo.'
    }
  },
  tabela_precos: {
    beneficios: ['Defina preços de produtos e adicionais por canal de venda.', 'Configure dias, horários e prioridade das tabelas.', 'Trabalhe com preços por quantidade nas tabelas de atacado.'],
  },
  comissionamento: {
    beneficios: ['Cadastre comissionados e comissões de valor fixo por produto.', 'Vincule os produtos a cada regra de comissão.', 'Consulte os valores apurados nos relatórios por período.'],
  },
  controle_fiado: {
    beneficios: ['Registre vendas no fiado e acompanhe o saldo de cada cliente.', 'Consulte os pedidos que compõem o valor em aberto.', 'Registre acertos e acompanhe o histórico de pagamentos.'],
  },
  mia_atendente: {
    beneficios: ['Configure respostas e fluxos de atendimento no WhatsApp.', 'Teste o comportamento da atendente antes de disponibilizá-la.', 'Consulte o histórico das conversas para acompanhar o atendimento.'],
    requisito: 'Requer a configuração do canal de WhatsApp da loja.',
  },
  mia_v3: {
    beneficios: ['Apresente produtos em uma conversa de venda no WhatsApp.', 'Ofereça adicionais enquanto o cliente monta o pedido.', 'Conduza a escolha dos itens até o fechamento no chat.'],
    requisito: 'A equipe apresenta a disponibilidade e configura o atendimento com o catálogo da sua loja.',
  },
  chatbot_instagram: {
    beneficios: ['Automatize respostas às mensagens recebidas no Instagram.', 'Direcione o cliente para os canais de venda da loja.', 'Use o atendimento digital para aproveitar o interesse nas redes sociais.'],
    requisito: 'A equipe verifica a conexão e a compatibilidade da conta do Instagram.',
  },
  notificacoes_whatsapp_pedidos: {
    beneficios: ['Informe o cliente sobre o andamento do pedido pelo WhatsApp.', 'Configure as mensagens usadas nas notificações.', 'Ajude o cliente a acompanhar a compra sem precisar perguntar o status.'],
    requisito: 'Requer a configuração do canal de WhatsApp e das notificações de pedidos.',
    previa: {
      imagem: 'assets/recursos/capturas/notificacoes-whatsapp-pedidos.jpg',
      titulo: 'WhatsApp: mensagem de pedido em preparação',
      legenda: 'Configuração real da notificação no ambiente de demonstração, com campo de nome do contato. Nenhuma mensagem foi enviada.',
    }
  },
  app_proprio: {
    beneficios: ['Ofereça um aplicativo com a identidade da sua marca.', 'Reúna pedidos e relacionamento com clientes em um canal próprio.', 'Facilite o retorno dos clientes ao catálogo da loja.'],
    requisito: 'A equipe apresenta o processo de criação do aplicativo e os recursos incluídos na contratação.',
  },
  dominio_proprio: {
    beneficios: ['Divulgue o cardápio usando um endereço com a sua marca.', 'Use o mesmo endereço nos materiais e nas redes sociais.', 'Facilite a identificação do canal oficial da loja.'],
    requisito: 'É necessário ter um domínio e configurar seu apontamento com a equipe.',
  },
  vitrines: {
    beneficios: ['Agrupe produtos em vitrines temáticas no cardápio.', 'Destaque combos, campanhas e seleções da loja.', 'Controle a ordem e a disponibilidade das vitrines.'],
    previa: {
      imagem: 'assets/recursos/capturas/vitrines.jpg',
      titulo: 'Vitrine real de produtos em destaque',
      legenda: 'Vitrine exibida pelo cardápio real da loja de demonstração.'
    }
  },
  cardapio_digital: {
    beneficios: ['Apresente produtos organizados por categorias.', 'Mostre fotos, descrições e preços em um endereço público.', 'Compartilhe o link do cardápio com seus clientes.'],
    previa: {
      imagem: 'assets/recursos/capturas/cardapio-digital.jpg',
      titulo: 'Cardápio real: produtos por categoria',
      legenda: 'Tela real do cardápio, com produtos e preços do ambiente de demonstração.'
    }
  },
  temas_personalizados: {
    beneficios: ['Escolha a aparência do cardápio de acordo com a marca.', 'Organize a apresentação dos produtos no tema escolhido.', 'Crie uma experiência visual coerente com a identidade da loja.'],
  },
  analytics_cardapio: {
    beneficios: ['Acompanhe acessos e conversão do cardápio.', 'Consulte o desempenho do canal digital.', 'Observe os visitantes em tempo real.'],
  },
  inteligencia_negocio: {
    beneficios: ['Consulte indicadores da operação em um painel.', 'Analise o comportamento das vendas por período.', 'Use os dados do negócio para apoiar decisões da loja.'],
    previa: {
      imagem: 'assets/recursos/capturas/inteligencia-negocio.jpg',
      titulo: 'Inteligência do Negócio: classificação de clientes',
      legenda: 'Painel real publicado na documentação, com distribuição por perfil e participação no faturamento. Indicadores são exemplos.',
    }
  },
  caixa: {
    beneficios: ['Registre a abertura e o fechamento do caixa.', 'Acompanhe vendas presenciais e movimentações do balcão.', 'Confira os registros financeiros da operação.'],
    previa: {
      imagem: 'assets/recursos/capturas/pdv-venda.jpg',
      titulo: 'Venda no PDV: produtos e montagem do pedido',
      legenda: 'Tela real do PDV com um produto do ambiente de demonstração. Nenhuma venda foi concluída.',
      outrasCapturas: [{
        imagem: 'assets/recursos/capturas/gestao-caixa.jpg',
        titulo: 'Gestão de Caixa: saldo e movimentações',
        legenda: 'Captura da documentação do sistema. Valores exibidos apenas como exemplo.',
      }]
    }
  },
  fiscal: {
    beneficios: ['Emita documentos fiscais das vendas da loja.', 'Acompanhe a situação dos documentos emitidos.', 'Centralize o controle fiscal associado à operação.'],
    requisito: 'A ativação depende da configuração fiscal da empresa, dos documentos exigidos e da orientação da equipe.',
    previa: {
      imagem: 'assets/recursos/capturas/fiscal-tributacao.png',
      titulo: 'Fiscal: configuração de tributação',
      legenda: 'Tela real publicada na documentação. Os campos ilustram a configuração do sistema; os valores são exemplos.',
    }
  },
  cupons_avancados: {
    beneficios: ['Crie campanhas usando cupons e promoções.', 'Defina regras de uso para controlar a oferta.', 'Organize ações promocionais de acordo com a estratégia da loja.'],
    previa: {
      imagem: 'assets/recursos/capturas/cupons-promocoes.jpg',
      titulo: 'Seleção real de cupons no pedido',
      legenda: 'Tela real de escolha de cupons no ambiente de demonstração. Código e condições são exemplos.'
    }
  },
  notificacoes_app_proprio: {
    beneficios: ['Envie notificações para os clientes do aplicativo da marca.', 'Divulgue campanhas no canal próprio da loja.', 'Convide os clientes a voltar para conhecer uma oferta.'],
    requisito: 'Requer o aplicativo próprio da marca e clientes habilitados para receber notificações.',
  },
  link_na_bio: {
    beneficios: ['Reúna os principais links da loja em uma página.', 'Destaque cardápio, redes sociais e canais de contato.', 'Divulgue um endereço único na bio dos seus perfis.'],
  },
  fidelidade: {
    beneficios: ['Crie campanhas de pontos e recompensas.', 'Estimule os clientes a voltar a comprar na loja.', 'Acompanhe a participação dos clientes nas campanhas.'],
    previa: {
      imagem: 'assets/recursos/capturas/fidelidade-planos.jpg',
      titulo: 'Fidelidade: planos de selos, pontos e cashback',
      legenda: 'Tela real de configuração dos planos publicada na documentação. Planos exibidos apenas como exemplo.',
      outrasCapturas: [{
        imagem: 'assets/recursos/capturas/pontuacoes-fidelidade.jpg',
        titulo: 'Fidelidade: consulta de pontuações por período',
        legenda: 'Tela real do relatório publicada na documentação. O exemplo mostra os filtros e a consulta sem resultados.',
      }]
    }
  },
  campanhas_whatsapp: {
    beneficios: ['Crie campanhas e selecione os contatos que vão recebê-las.', 'Envie testes e aprove o conteúdo das mensagens.', 'Acompanhe o histórico dos envios.'],
    requisito: 'A equipe apresenta as condições do canal e a configuração dos envios.',
  },
  ferramentas_marketing: {
    beneficios: ['Configure as ferramentas de medição do marketing digital.', 'Conecte identificadores de plataformas de análise e anúncios.', 'Acompanhe as ações digitais nas ferramentas integradas.'],
    requisito: 'Requer contas e identificadores das ferramentas que a loja deseja configurar.',
  },
  pedidos_delivery: {
    beneficios: ['Receba pedidos feitos pelo cardápio digital.', 'Acompanhe carrinho, checkout e andamento dos pedidos.', 'Centralize a gestão da operação de delivery.'],
    previa: {
      imagem: 'assets/recursos/capturas/pedidos-delivery.jpg',
      titulo: 'Resumo real do pedido na sacola',
      legenda: 'Sacola do sistema com um produto de demonstração. Nenhum pedido foi enviado.'
    }
  },
  avaliacoes_pedidos: {
    beneficios: ['Colete avaliações após os pedidos.', 'Acompanhe a percepção dos clientes sobre a experiência.', 'Use os comentários para identificar melhorias na operação.'],
  },
  mesas_garcom: {
    beneficios: ['Organize pedidos de mesas e comandas.', 'Ofereça o QR Code da mesa para o atendimento presencial.', 'Acompanhe o consumo associado a cada mesa.'],
    previa: {
      imagem: 'assets/recursos/capturas/mesas-visao-geral.jpg',
      titulo: 'Mesas e comandas: acompanhamento do consumo',
      legenda: 'Tela real de mesas publicada na documentação. Mesas e valores exibidos apenas como exemplo.',
    }
  },
  atendimento_garcom: {
    beneficios: ['Permita que a equipe lance pedidos durante o atendimento.', 'Associe os pedidos à mesa ou à comanda correta.', 'Organize a participação dos garçons no fluxo da loja.'],
    requisito: 'A equipe configura os usuários e o fluxo de mesas e comandas da operação.',
  },
  entregas_logistica: {
    beneficios: ['Organize os entregadores da loja.', 'Acompanhe as entregas da operação.', 'Centralize o controle da logística dos pedidos.'],
  },
  impressao_avancada: {
    beneficios: ['Configure a impressão automática dos pedidos.', 'Separe impressões por setor de produção.', 'Conheça as opções de impressão pelo celular.'],
    requisito: 'A equipe verifica impressoras, dispositivos e configuração dos setores da loja.',
    previa: {
      imagem: 'assets/recursos/capturas/impressao-config.jpg',
      titulo: 'Impressão: formato e momento de imprimir',
      legenda: 'Configurações reais de impressão publicadas na documentação: formato do pedido e acionamento automático.',
    }
  },
  pagamentos_eletronicos: {
    beneficios: ['Integre o pagamento presencial ao terminal.', 'Acompanhe o resultado da transação no fluxo da venda.', 'Reduza a digitação manual do valor na operação integrada.'],
    requisito: 'A equipe verifica o provedor, o terminal e os requisitos de instalação compatíveis com a loja.',
  },
  integracao_gateway_pagamento: {
    beneficios: ['Conheça as opções de pagamento online integradas.', 'Conecte o provedor de pagamento ao fluxo de compra.', 'Acompanhe os pagamentos associados aos pedidos.'],
    requisito: 'A equipe confirma provedores, modalidades e condições disponíveis para a sua loja.',
  },
  integracao_pdv_parceiros: {
    beneficios: ['Conheça as possibilidades de conexão com o seu PDV.', 'Reduza a repetição de informações entre sistemas compatíveis.', 'Alinhe o fluxo de pedidos à operação já usada pela loja.'],
    requisito: 'A equipe precisa confirmar o sistema parceiro e o escopo da integração antes da contratação.',
  },
  integracao_delivery_parceiros: {
    beneficios: ['Conheça as conexões com plataformas parceiras de delivery.', 'Organize o fluxo dos pedidos dos canais integrados.', 'Reduza o trabalho manual entre sistemas compatíveis.'],
    requisito: 'A equipe confirma quais plataformas e operações estão disponíveis para a sua loja.',
  },
  integracao_marketplaces_delivery: {
    beneficios: ['Conheça as opções para receber pedidos de marketplaces.', 'Centralize o acompanhamento dos canais compatíveis.', 'Organize a operação de pedidos vindos de diferentes plataformas.'],
    requisito: 'A equipe confirma os marketplaces disponíveis e a compatibilidade da conta da loja.',
  },
  estoque: {
    beneficios: ['Acompanhe produtos e insumos usados na operação.', 'Registre movimentações e consulte o histórico do estoque.', 'Associe insumos aos produtos para organizar o controle.'],
    previa: {
      imagem: 'assets/recursos/capturas/estoque-insumos.jpg',
      titulo: 'Estoque: insumos, quantidades e estoque mínimo',
      legenda: 'Tela real de insumos publicada na documentação. Quantidades exibidas apenas como exemplo.',
      outrasCapturas: [{
        imagem: 'assets/recursos/capturas/estoque-ficha-tecnica.jpg',
        titulo: 'Estoque: fichas técnicas dos produtos',
        legenda: 'Tela real de fichas técnicas, usada para associar insumos aos produtos. Captura da documentação.',
      }]
    }
  }
};
